/**
 * Created by kevin on 2016-12-07.
 *
 *  Numeric form field implementation; includes display formatting from an enumerated list when in read-only mode
 *
 */

import { Component, Input, OnChanges, SimpleChange } from '@angular/core';
import { FormGroup } from '@angular/forms';

import { AbstractValueAccessor, MakeProvider } from './abstract-value-accessor';
import { DynamicFormFieldComponent } from './dynamic-form-field.component';
import { FormField } from '../models/form-field';
import { LocalizeService } from '../services/localize.service';

@Component({
  selector: 'edx-numeric-field',
  providers: [MakeProvider(NumericFieldComponent)],
  styleUrls: ['dynamic-form-field.component.scss'],
  template: `
  <div [formGroup]="form" class="controlcontainer" [title]="toolTip">
    @if (!viewReadonly) {
      <input 
        type="text"
        (input)="onInputChange($event)"
        class="numberinput"
        [ngClass]="{'admin-system-textbox':parent.isSystemParamsEditField(), 'admin-group-textbox':parent.isGroupParamsEditField(), 'preview_only': parent?.previewOnly}"
        placeholder="{{placeholder}}"
        role="textbox"
        [id]="field.name"
        [attr.aria-labelledby]="getInputLabelId()"
        step="any"
        [readonly]="viewReadonly ? true : null"
        [edx-validate-onblur]="!!parent ? parent.wantsValidateOnBlur() : false"
        [min]="field.minVal >= 0 ? field.minVal : null"
        [max]="field.maxVal > 0 ? field.maxVal : null" [tabindex]="tabIndex"
        [required]="field.isRequired ? true : null"
        [(value)]="value"
        [formControlName]="field.name"
        (blur)="editNumberBlur($event)">
    }
  
    @if (parent.isSystemParamsEditField()) {
      <span [title]="field.label" class="checklabel checkboxlabel" [attr.aria-label]="field.label" [id]="getInputLabelId()">{{field.label}}</span>
    }
    <ng-content class="lookupbutton" select="[lookupButton]"></ng-content>
    @if (canShowErrorMessage()) {
      <div class="errormessage">{{field.errormessage}}</div>
    }
    @if (viewReadonly) {
      <span class="formattednumeric" [ngClass]="{columnview:columnView}">{{displayValue}}</span>
    }
  </div>
  `,
  standalone: false
})
export class NumericFieldComponent extends AbstractValueAccessor implements OnChanges {
  @Input() field: FormField;
  @Input() form: FormGroup;
  @Input() placeholder: string;
  @Input() formReadonly?: boolean = false;
  @Input() columnView?: boolean = false;
  @Input() toolTip?: string = '';
  @Input() rerender = 0;
  @Input() tabIndex = 0;
  @Input() parent: DynamicFormFieldComponent;
  public viewReadonly: boolean;
  private displayValue = '';

  constructor(private localizer: LocalizeService) {
    super();
  }

  ngOnChanges(changes: { [propertyName: string]: SimpleChange }) {
    this.viewReadonly = this.field.isReadonly || this.formReadonly;
    this.value = this.form.controls[this.field.name].value;
    if (!!this.field.lookupData) {
      if (this.field.lookupData.length === 1) {
        // Single hit on lookup
        this.value = this.field.lookupData[0]?.id;
      } else if (this.field.lookupData.length > 1 && !!this.field.parentField) {
        this.value = this.field.lookupData[0]?.id;
      } else {
        this.value = '';
      }
      if (!!this.parent) {
        this.parent.updateControlValue(this.value, true);
        if (!!this.parent && !!this.parent.parent && this.field.lookupData.length > 0) {
          this.parent.parent.setFieldValueAndDescOfParents(this.field, this.field.lookupData[0]['data']);
        }
      }
    }
    let display = '';
    if (typeof this.value === 'boolean') {
      this.value = this.localizer.getTranslation(this.value ? 'FORMS.BUTTONS.YES' : 'FORMS.BUTTONS.NO');
    }
    if (this.viewReadonly && (this.field.name !== 'DOCNUM' || !isNaN(+this.value))) {
      const selector: string = !!this.value ? this.value.toString() : '';
      display = selector;
      if (this.field.format && this.field.formatMap && this.field.formatMap.length) {
        for (const entry of this.field.formatMap) {
          if (entry.value === selector) {
            display = entry.display;
          }
        }
      }
      this.displayValue = display;
    }
  }

  onInputChange(event: Event) {
    const input = event.target as HTMLInputElement;
    let val = input.value;
    let cursorPos = input.selectionStart ?? 0;
    const invalidBeforeCursor = (val.slice(0, cursorPos).match(/[^0-9-]|(?!^)-/g) || []).length;
    val = val.replace(/(?!^)-/g, '').replace(/[^0-9-]/g, ''); // Keep only digits and an optional leading '-'
    cursorPos = Math.max(0, cursorPos - invalidBeforeCursor);
    input.value = val;
    this.form.controls[this.field.name].setValue(val);
    this.value = val;
    input.setSelectionRange(cursorPos, cursorPos);
    if (this.parent.isAdminGroupEditFormField()) {
      this.parent.parent?.setLastAccessedSectionForChild(this.field.name);
    }
  }

  public editNumberBlur(event: Event): void {
    const blurValue = this.form.controls[this.field.name].value;
    if (!blurValue || (blurValue && blurValue === '')) {
      if (!!this.parent) {
        this.parent.fieldChanged();
      }
    }
  }

  public canShowErrorMessage(): boolean {
    const isGroupParamsEditHasError = this.parent.isGroupParamsEditField() ? !!this.field.errormessage : true;
    const isSysParamsEditHasError = this.parent.isSystemParamsEditField() ? !!this.field.errormessage : true;
    return !this.viewReadonly && isSysParamsEditHasError && isGroupParamsEditHasError;
  }

  public getInputLabelId(): string {
    return this.field.name + '_label';
  }
}
